import { React, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Row from "components/globals/ui/Row";
import parse from "html-react-parser";
import PSPage from "components/globals/ui/layout/Page"
import { useFetchData, scrollToComments } from "helpers/utils";
import IsYoursBanner from 'components/globals/IsYoursBanner';
import {ORSMasterListing} from "components/globals/ORSMasterListing"
import ORSYearMakeModel from "components/globals/car/ORSYearMakeModel";
import CarNotes from "components/globals/car/CarNotes";
import Comments from "components/globals/comments/Comments";
import { getTypeString, getCategoryString } from "types/carTypes";
import Likes from "components/globals/likes/Likes";
import { FaRegCommentAlt } from "react-icons/fa";
import Carousel from "components/globals/image/Carousel";
import {CategoryBadge, TypeBadge} from "components/globals/ui/TypeBadge";
import {ORSSubnav, SubnavItem} from "components/globals/ORSSubnav";
import Single from "components/globals/image/Single";
import CarGalleries from "components/globals/car/CarGalleries";
import CarMods from "components/globals/car/CarMods";
import { BsSpeedometer } from "react-icons/bs";
import { GiCarWheel } from "react-icons/gi";
import { PiEngineBold } from "react-icons/pi";
import { LuPaintbrush } from "react-icons/lu";
import { BiCategory, BiCategoryAlt } from "react-icons/bi";


const StickyNavContainer = ({children}) => {
  return (
    <div className="hidden md:flex text-[13px] overflow-hidden bg-brg text-white sticky top-[70px] items-center justify-between rounded-default  z-[5]">
      {children}
    </div>
  )
}

const StickyNavItem = ({activeSection, scrollToSection, label, id}) => {
  return (
    <span 
      className={`py-4 grow text-center cursor-pointer hover:bg-brg-dark transition-colors border-[1px] border-brg-light ${
        activeSection === id ? 'bg-brg-dark' : ''
      }`}
      onClick={() => scrollToSection(id)}
    >
      {label}
    </span>
  )
}

const UserCar = () => {
  const { id } = useParams();
  const [activeSection, setActiveSection] = useState('overview');

  // double entry, entry.entry & entry.user
  const { data: entry, loading, error } = useFetchData(
		`${process.env.REACT_APP_SERVER_URI}/api/car/${id}`,
		[id],
	);

  const rowOptions = {
    layout: "row",
    yoursBadge: false,
    typeBadge: false,
    categoryBadge: false,
    userBadge: false,
    carBadge: false,
    carDetails: false,
    dateDetails: false,
    rowColumns: true,
    mobileCarousel: false,
    likes: true,
    shortRow: true,
  }

  // Scroll to section function
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Track active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'galleries', 'mods', 'feed', 'garage', 'additional'];
      const scrollPosition = window.scrollY + 150; // Offset for sticky header

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i]);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once to set initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, [entry]); // Re-run when entry data loads

  const getSections = (entryData) => [
    {
      params: {
				elementId: "carFeed",
				headingTitle: `Car Feed for: ${entryData?.entry?.title}`,
				headingButtonUrl: "",
				headingButtonText: "",
				apiPath: `post/car/${entryData?.entry?.internal_id}`,
				omit: "none",
				limit: "24",
				type: "post", 
				pagination: true,
			},
      displayOptions: {
        layout: "card",
        yoursBadge: false,
        typeBadge: true,
        categoryBadge: false,
        userBadge: false,
        carBadge: false,
        carDetails: false,
        dateDetails: true,
        rowColumns: true,
        mobileCarousel: false,
        likes: true,
      }
    },
    {
      params: {
				elementId: "otherGarageCars", 
        headingTitle: `Other cars in ${entryData?.user?.username}'s garage`,
				headingButtonUrl: "",
				headingButtonText: "",
				apiPath: `garage/user/${entryData?.user?.user_id}`,
				omit: entryData?.entry?.internal_id,
				limit: "12",
				type: "car", 
				pagination: true,
			},
      displayOptions: {
        layout: "row",
        yoursBadge: false,
        typeBadge: true,
        categoryBadge: true,
        userBadge: false,
        carBadge: false,
        carDetails: true,
        dateDetails: false,
        rowColumns: true,
        mobileCarousel: false,
        likes: true,
      }
    },
    { 
      params: {
				elementId: "relatedCars", 
        headingTitle: `Other ${entryData?.entry?.make} cars on ORS`,
				headingButtonUrl: `/cars/make/${entryData?.entry?.make?.toLowerCase()}`,
				headingButtonText: "View All",
				apiPath: `garage/related/make/${entryData?.entry?.make?.toLowerCase()}`,
				omit: entryData?.entry?.internal_id,
				limit: "12",
				type: "car", 
				pagination: false,
			},
      displayOptions: {
        layout: "card",
        yoursBadge: true,
        typeBadge: false,
        categoryBadge: false,
        userBadge: true,
        carBadge: false,
        carDetails: true,
        dateDetails: false,
        rowColumns: true,
        mobileCarousel: false,
        likes: true,
      }
    },
  ];

  // Loading & error states
	if (loading) return <div>Loading...</div>;
	if (error) return <div>Error: {error.message}</div>;


  const TopLevelCarItem = ({Icon, value, title}) => {
    return (
      <div className="w-[47%] min-w-atuo md:min-w-[250px] md:w-auto border-grey border-[1px] p-3 rounded-default ">
        <Icon className="text-grey mb-2 text-sm md:text-xl" />
        <span className="block text-sm md:text-xl  text-grey">{value}</span>
        <span className="block text-xs  text-grey opacity-[.5] mt-1">{title}</span>
      </div>
    )
  }

  const sections = getSections(entry);
  const [carFeedSection, garageSection, additionalSection] = sections;


  return (
    <>
      <ORSSubnav>
        <SubnavItem to="/cars" label="Cars Home" />
        <SubnavItem to="/cars/brands" label="Brands" />
        <SubnavItem to="/cars/users" label="User Cars" />
        <SubnavItem to="/cars/spotted" label="Spotted" />
      </ORSSubnav>

      <IsYoursBanner label="Your Car" entry={entry.entry} />

      <div 
        className="
          flex 
          items-start 
          flex-wrap 
          md:flex-nowrap 
          md:py-xs 
          md:pt-sm 
          w-full
          md:w-[95%]
          max-w-[1200px]
          mx-auto">

        <Single
          className="relative aspect-[4/2.7] w-full md:w-1/2 grow shrink-0 md:rounded-default overflow-hidden"
          image={
            Array.isArray(entry.entry.profile) && entry.entry.profile.length > 0
              ? entry.entry.profile[0]
              : Array.isArray(entry.entry.gallery) && entry.entry.gallery.length > 0
                ? entry.entry.gallery[0]
                : null
          }
        />

        <div 
          className="
            w-full 
            md:w-1/2 
            grow 
            shrink-0 
            relative 
            md:pl-md 
            md:pr-sm 
            px-xs
            py-sm 
            md:py-0
            rounded-3xl
            -mt-[20px]
            md:mt-0
            bg-background"
          >

          <div className="absolute md:top-0 md:right-3 top-sm right-xs">
            <Likes 
              entry_type={entry.entry.entry_type} 
              entry_id={entry.entry.internal_id}
            />
          </div>

          <h1 className="m-0 text-2xl md:text-3xl  md:mb-2 pr-[50px]">{entry.entry.title}</h1>
          <div className="my-1 inline-block flex items-center gap-2 flex-wrap md:flex-nowrap">
            <TypeBadge 
              entryType={entry.entry.entry_type} 
              type={entry.entry.type}
            />

            <CategoryBadge 
              entryType={entry.entry.entry_type} 
              type={entry.entry.type} 
              category={entry.entry.category}
            />
          </div>

          <div className="mt-3">
            <ORSYearMakeModel entry={{
              year: entry.entry.year,
              make: entry.entry.make,
              model: entry.entry.model,
              trim: entry.entry.trim
            }} />
          </div>

          <div className="my-3">
            <p className="text-[12px] tracking-[.2px]  mb-1">Owner</p>
            <Row
              item={entry.user}
              displayOptions={rowOptions}
              id={`item-${entry.user.user_id}`}
            />
          </div>

          <div 
            className="cursor-pointer flex items-center mt-3" 
            onClick={scrollToComments}
          >
            <FaRegCommentAlt className="text-black text-xl mr-2" />
            <span className=" text-[11px]">Comments</span>
          </div>
        </div>

      </div>

      <PSPage collapseBottom>

        <StickyNavContainer>
          <StickyNavItem activeSection={activeSection} scrollToSection={scrollToSection} id='overview' label='Overview' />
          <StickyNavItem activeSection={activeSection} scrollToSection={scrollToSection} id='galleries' label='Galleries' />
          <StickyNavItem activeSection={activeSection} scrollToSection={scrollToSection} id='mods' label='Mods' />
          <StickyNavItem activeSection={activeSection} scrollToSection={scrollToSection} id='feed' label='Car Feed' />
          <StickyNavItem activeSection={activeSection} scrollToSection={scrollToSection} id='garage' label='Stablemates' />
          <StickyNavItem activeSection={activeSection} scrollToSection={scrollToSection} id='additional' label='Other Cars' />
        </StickyNavContainer>
        

        {/* top-level stats */}
        <div className="mb-md" id="overview">
          <h3 className=" text-2xl mt-6 mb-3">Overview</h3>

          <div className="flex flex-wrap gap-3 pt-xs">
            {entry.entry.horsepower && (
              <TopLevelCarItem title="Horsepower" value={entry.entry.horsepower} Icon={BsSpeedometer} />
            )}
            {entry.entry.torque && (
              <TopLevelCarItem title="Torque" value={entry.entry.torque} Icon={GiCarWheel} />
            )}
            {entry.entry.engine && (
              <TopLevelCarItem title="Engine" value={entry.entry.engine} Icon={PiEngineBold} />
            )}
            {entry.entry.mileage && (
              <TopLevelCarItem title="Mileage" value={entry.entry.mileage} Icon={GiCarWheel} />
            )}
            {entry.entry.color && (
              <TopLevelCarItem title="Color" value={entry.entry.color} Icon={LuPaintbrush} />
            )}
            {entry.entry.type && (
              <TopLevelCarItem title="Type" value={getTypeString(entry.entry.type)} Icon={BiCategory} />
            )}
            {entry.entry.category && (
              <TopLevelCarItem title="Category" value={getCategoryString(entry.entry.type, entry.entry.category)} Icon={BiCategoryAlt} />
            )}

          </div>

          <div className="my-md">
            {entry?.entry?.body && (
              <div >
                <div className="mt-3 ors-rich-text">{parse(entry?.entry?.body)}</div>
              </div>
            )}
          </div>

        </div>
    
        <div className="my-md" id="galleries">
          <h3 className=" text-xl mt-6 mb-3">Galleries</h3>
          {entry.entry.gallery && entry.entry.gallery.length > 2 && (
            <div className="my-sm w-full gallery-adjust">
              <Carousel gallery={entry.entry.gallery.slice(1)} />
            </div>
          )}

          <CarGalleries id={id} />
        </div>
        
        <div className="my-md" id="mods">
          <CarMods id={id} />
        </div>


        <div className="my-md" id="feed">
          {carFeedSection && (
            <ORSMasterListing 
              key={`${carFeedSection.params.elementId}-${id}`} 
              params={carFeedSection.params} 
              displayOptions={carFeedSection.displayOptions} 
            />
          )}
        </div>


        {/* notes */}
        <CarNotes entry={entry.entry} />

        <div className="my-md" id="garage">
          <h3 className=" text-2xl mt-6 mb-3">{}</h3>
          {garageSection && (
            <ORSMasterListing 
              key={`${garageSection.params.elementId}-${id}`} 
              params={garageSection.params} 
              displayOptions={garageSection.displayOptions} 
            />
          )}
        </div>

        <div className="my-md" id="additional">
          {additionalSection && (
            <ORSMasterListing 
              key={`${additionalSection.params.elementId}-${id}`} 
              params={additionalSection.params} 
              displayOptions={additionalSection.displayOptions} 
            />
          )}
        </div>
              
        <div id="comments">
          <Comments 
            entry_type={entry.entry.entry_type} 
            entry_id={entry.entry.internal_id}
          />
        </div>

      </PSPage>
    </>
  );
};

export default UserCar;